import * as React from 'react';
import { getSupabase } from '@/lib/supabase';
import { resolveAuthPortalRole } from '@/utils/resolveAuthPortalRole';
import { syncSessionToServer } from '@/data/authApi';

/**
 * Password reset completion page.
 *
 * Flow:
 *   1. User enters their email on CoachAuth or ClientSignIn "Forgot password?".
 *   2. We call supabase.auth.resetPasswordForEmail with redirectTo set to
 *      /auth/callback.
 *   3. Supabase sends a recovery email with a token_hash link.
 *   4. AuthCallback verifyOtp(type=recovery) creates a recovery session, then
 *      redirects to /reset-password.
 *   5. This page requires that session, renders the new-password form, and
 *      calls supabase.auth.updateUser({ password }) to finalize the change.
 *
 * Defense-in-depth: if the user lands here without a session (e.g. pasted the
 * URL directly or let the recovery session expire), we redirect them back to
 * the appropriate sign-in page with a friendly error.
 */
export default function ResetPassword() {
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [checkingSession, setCheckingSession] = React.useState(true);
  const [hasSession, setHasSession] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  // Confirm we have a session (recovery sessions behave like normal sessions).
  // Without a session there's nothing to update, so we don't render the form.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) {
        if (!cancelled) {
          setError('Auth not configured');
          setCheckingSession(false);
        }
        return;
      }
      const { data } = await sb.auth.getSession();
      if (cancelled) return;
      setHasSession(!!data.session);
      setCheckingSession(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // AuthCallback forwards the resolved role (?role=coach|client) and a
  // mismatch flag when the user started the flow from the opposite portal.
  // Fall back to the stamped localStorage key for direct loads (e.g. user
  // refreshed this page after recovery completed).
  const { roleFromUrl, mismatch } = ((): { roleFromUrl: 'coach' | 'client' | null; mismatch: boolean } => {
    try {
      const params = new URLSearchParams(window.location.search);
      const r = params.get('role');
      const role: 'coach' | 'client' | null = r === 'coach' || r === 'client' ? r : null;
      return { roleFromUrl: role, mismatch: params.get('mismatch') === '1' };
    } catch {
      return { roleFromUrl: null, mismatch: false };
    }
  })();

  const storedUserType = (() => {
    try {
      return localStorage.getItem('coachd:userType');
    } catch {
      return null;
    }
  })();

  const resolvedRole: 'coach' | 'client' | null =
    roleFromUrl || (storedUserType === 'coach' || storedUserType === 'client' ? storedUserType : null);

  const signInHref = resolvedRole === 'client' ? '/client/auth' : '/coach/sign-in';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const sb = getSupabase();
    if (!sb) {
      setError('Auth not configured');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await sb.auth.updateUser({ password });
      if (updateError) throw updateError;

      // Re-sync the session to Worker cookies so server endpoints see the
      // freshly authenticated user (the password update rotates tokens).
      const { data: sessionData } = await sb.auth.getSession();
      if (sessionData.session) {
        await syncSessionToServer(sessionData.session);
      }

      // Re-resolve role post-update. The recovery session may have been
      // seeded by AuthCallback with the right coachd:userType, but we
      // double-check here so the post-reset landing always picks the correct
      // side — even if the user refreshed this page or localStorage was
      // cleared mid-flow.
      let finalRole: 'coach' | 'client' | null = resolvedRole;
      try {
        const user = sessionData.session?.user ?? null;
        if (user) {
          const fresh = await resolveAuthPortalRole(sb, user);
          if (fresh) finalRole = fresh;
        }
      } catch {
        /* keep existing finalRole */
      }
      if (finalRole) {
        try {
          localStorage.setItem('coachd:userType', finalRole);
        } catch {
          // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
        }
      }

      setSuccess(true);
      // Land on /?confirmed=1 so the post-login router picks the right home;
      // coachd:userType above steers client vs coach UI.
      setTimeout(() => window.location.replace('/?confirmed=1'), 1200);
    } catch (err: any) {
      setError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0E10] text-[#EDEDEF] flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Reset your password</h1>
          <p className="text-[#AEAEB0]">Choose a new password for your account.</p>
        </div>

        {checkingSession ? (
          <div className="bg-[#1C1C1E] rounded-lg p-6 border border-[#2C2C2E] text-center text-sm text-[#AEAEB0]">
            Verifying your reset link…
          </div>
        ) : !hasSession ? (
          <div className="bg-[#1C1C1E] rounded-lg p-6 border border-[#2C2C2E] text-center space-y-4">
            <p className="text-sm text-[#AEAEB0]">
              Your password reset link is invalid or has expired. Please request a new one.
            </p>
            <a
              href={signInHref}
              className="inline-block rounded-lg bg-[#0A84FF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0A84FF]/90"
            >
              Back to sign in
            </a>
          </div>
        ) : success ? (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
            <p className="text-green-400">Password updated. Signing you in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 bg-[#1C1C1E] rounded-lg p-6 border border-[#2C2C2E]">
            {mismatch && resolvedRole ? (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm text-amber-300">
                Heads up: this email belongs to the <span className="font-semibold">{resolvedRole}</span> portal. After
                updating your password you'll be taken to the {resolvedRole} side.
              </div>
            ) : null}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
            )}

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium mb-2">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-[#2C2C2E] bg-[#0E0E10] px-4 py-3 text-[#EDEDEF] placeholder-[#6E6E73] focus:border-[#0A84FF] focus:outline-none focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="At least 8 characters"
                required
                minLength={8}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium mb-2">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-lg border border-[#2C2C2E] bg-[#0E0E10] px-4 py-3 text-[#EDEDEF] placeholder-[#6E6E73] focus:border-[#0A84FF] focus:outline-none focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="Re-enter your password"
                required
                minLength={8}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full rounded-lg bg-[#0A84FF] px-4 py-3 font-medium text-white hover:bg-[#0A84FF]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-[#6E6E73]">
          By continuing you agree to our{' '}
          <a
            href="https://os-coach.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#AEAEB0] hover:text-[#EDEDEF] underline underline-offset-2"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://os-coach.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#AEAEB0] hover:text-[#EDEDEF] underline underline-offset-2"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
